import React from 'react';
import styled from 'styled-components';
import SEO from '../../components/common/SEO';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaMoneyBillWave, FaClock, FaChartLine, FaSync, FaMobileAlt, FaLeaf } from 'react-icons/fa';
import theme from '../../theme';

const BenefitsSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  margin-bottom: 5rem;
`;

const BenefitCard = styled.div`
  display: flex;
  background-color: ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  margin-bottom: 2rem;
  box-shadow: ${theme.boxShadow};
  transition: ${theme.transition};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const BenefitIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  font-size: 3rem;
  padding: 2rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-width: 100%;
    padding: 1.5rem;
  }
`;

const BenefitContent = styled.div`
  padding: 2rem;
`;

const BenefitTitle = styled.h3`
  font-size: ${theme.fontSizes.large};
  margin-bottom: 1rem;
  color: ${theme.colors.secondary};
`;

const BenefitDescription = styled.p`
  color: ${theme.colors.darkGray};
  line-height: 1.7;
  margin-bottom: 1rem;
`;

const BenefitList = styled.ul`
  padding-left: 1.5rem;
  margin-bottom: 0;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
`;

const TestimonialSection = styled.section`
  padding: 4rem 0;
  background-color: ${theme.colors.lightGray};
`;

const TestimonialTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  font-size: ${theme.fontSizes.xlarge};
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.div`
  background-color: ${theme.colors.white};
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  position: relative;
  
  &:before {
    content: '"';
    position: absolute;
    top: 10px;
    left: 15px;
    font-size: 4rem;
    color: ${theme.colors.primary};
    opacity: 0.2;
    font-family: Georgia, serif;
  }
`;

const TestimonialContent = styled.p`
  font-style: italic;
  margin-bottom: 1.5rem;
  line-height: 1.7;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.h4`
  margin-bottom: 0.25rem;
`;

const AuthorTitleText = styled.p`
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.darkGray};
  margin-bottom: 0;
`;

const CTASection = styled.section`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  max-width: 700px;
  margin: 0 auto 2rem;
  font-size: ${theme.fontSizes.medium};
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 1rem 2rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const benefitIcons = [
  <FaMoneyBillWave />,
  <FaClock />,
  <FaChartLine />,
  <FaSync />,
  <FaMobileAlt />,
  <FaLeaf />
];

const benefitKeys = ['costSaving', 'timeSaving', 'salesGrowth', 'processEfficiency', 'digitalTransformation', 'sustainability'];

const BenefitsPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const testimonials = [1, 2, 3, 4].map((num) => ({
    content: t(`references.testimonials.items.testimonial${num}.text`),
    author: t(`references.testimonials.items.testimonial${num}.author`),
    title: `${t(`references.testimonials.items.testimonial${num}.position`)}, ${t(`references.testimonials.items.testimonial${num}.company`)}`,
    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(t(`references.testimonials.items.testimonial${num}.author`))}&background=random&size=100`
  }));
  
  return (
    <>
      <SEO titleKey="benefitsPage.seo.title" descriptionKey="benefitsPage.seo.description" />
      
      <BenefitsSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('benefitsPage.hero.title')}</PageTitle>
            <PageSubtitle>{t('benefitsPage.hero.subtitle')}</PageSubtitle>
          </PageHeader>
          
          <ContentSection>
            {benefitKeys.map((key, index) => (
              <BenefitCard key={key}>
                <BenefitIcon>{benefitIcons[index]}</BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>{t(`benefitsPage.items.${key}.title`)}</BenefitTitle>
                  <BenefitDescription>{t(`benefitsPage.items.${key}.description`)}</BenefitDescription>
                  <BenefitList>
                    {[1, 2, 3, 4].map((num) => (
                      <li key={num}>{t(`benefitsPage.items.${key}.item${num}`)}</li>
                    ))}
                  </BenefitList>
                </BenefitContent>
              </BenefitCard>
            ))}
          </ContentSection>
        </div>
      </BenefitsSection>
      
      <TestimonialSection>
        <div className="container">
          <TestimonialTitle>{t('benefitsPage.testimonials.title')}</TestimonialTitle>
          <TestimonialGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <TestimonialContent>{testimonial.content}</TestimonialContent>
                <TestimonialAuthor>
                  <AuthorAvatar>
                    <img src={testimonial.avatar} alt={testimonial.author} loading="lazy" />
                  </AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorTitleText>{testimonial.title}</AuthorTitleText>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialGrid>
        </div>
      </TestimonialSection>
      
      <CTASection>
        <div className="container">
          <CTATitle>{t('benefitsPage.cta.title')}</CTATitle>
          <CTAText>{t('benefitsPage.cta.description')}</CTAText>
          <CTAButton to={`/${lang}/demo`}>{t('benefitsPage.cta.button')}</CTAButton>
        </div>
      </CTASection>
    </>
  );
};

export default BenefitsPage;